import { ChevronLeft, ChevronRight } from "lucide-react";
import type { WheelEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { WorkImage, WorkProject } from "../content/workImages";
import { SectionHeading } from "./SectionHeading";

export function WorkGallery({ images }: { images: readonly WorkImage[] }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(images.length > 1);

  const updateScrollState = useCallback(() => {
    const track = trackRef.current;

    if (!track) return;

    setCanScrollBack(track.scrollLeft > 1);
    setCanScrollForward(
      track.scrollLeft + track.clientWidth < track.scrollWidth - 1
    );
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    updateScrollState();
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(track);

    return () => observer.disconnect();
  }, [updateScrollState]);

  function scroll(direction: -1 | 1) {
    const track = trackRef.current;
    if (!track) return;

    track.scrollBy({
      behavior: "smooth",
      left: direction * Math.max(track.clientWidth * 0.72, 280)
    });
  }

  function handleWheel(event: WheelEvent<HTMLDivElement>) {
    const track = trackRef.current;
    if (!track || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;

    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
    const movingIntoTrack =
      (event.deltaY < 0 && !atStart) || (event.deltaY > 0 && !atEnd);

    if (movingIntoTrack) {
      event.preventDefault();
      track.scrollLeft += event.deltaY;
    }
  }

  return (
    <section className="work-gallery section-pad" data-reveal>
      <div className="work-gallery-header">
        <SectionHeading
          eyebrow="Selected Work"
          title="Fabricated products in finished environments."
        />
        <div className="work-gallery-controls" aria-label="Gallery controls">
          <button
            aria-label="Previous gallery images"
            disabled={!canScrollBack}
            onClick={() => scroll(-1)}
            type="button"
          >
            <ChevronLeft aria-hidden="true" size={20} />
          </button>
          <button
            aria-label="Next gallery images"
            disabled={!canScrollForward}
            onClick={() => scroll(1)}
            type="button"
          >
            <ChevronRight aria-hidden="true" size={20} />
          </button>
        </div>
      </div>
      <div
        aria-label="Selected fabricated products"
        className="work-gallery-track"
        onScroll={updateScrollState}
        onWheel={handleWheel}
        ref={trackRef}
        role="region"
        tabIndex={0}
      >
        {images.map((image) => (
          <WorkImageCard image={image} key={image.id} />
        ))}
      </div>
    </section>
  );
}

export function WorkFeature({ project }: { project: WorkProject }) {
  const [heroImage, ...supportingImages] = project.images;

  return (
    <section className="work-feature section-pad" data-reveal>
      <figure>
        <div className="work-feature-media">
          <img
            alt={heroImage.alt}
            decoding="async"
            height={heroImage.height}
            loading="lazy"
            src={heroImage.src}
            width={heroImage.width}
          />
          {supportingImages.length > 0 ? (
            <div
              aria-label={`${project.title} additional views`}
              className="work-feature-thumbnails"
            >
              {supportingImages.map((image) => (
                <img
                  alt={image.alt}
                  decoding="async"
                  height={image.height}
                  key={image.id}
                  loading="lazy"
                  src={image.src}
                  width={image.width}
                />
              ))}
            </div>
          ) : null}
        </div>
        <figcaption>
          <span>Selected client work</span>
          <strong>{project.title}</strong>
          <p>{project.description}</p>
        </figcaption>
      </figure>
    </section>
  );
}

function WorkImageCard({ image }: { image: WorkImage }) {
  return (
    <figure className="work-gallery-card">
      <img
        alt={image.alt}
        decoding="async"
        height={image.height}
        loading="lazy"
        src={image.src}
        width={image.width}
      />
      <figcaption>
        <strong>{image.name}</strong>
        <p>{image.description}</p>
      </figcaption>
    </figure>
  );
}
